import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  FormControlLabel,
  Avatar,
  Stack,
  useTheme,
  Slider,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Checkbox,
  FormGroup,
  InputAdornment,
  ClickAwayListener,
  Radio
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import LanguageIcon from '@mui/icons-material/Language';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TuneIcon from '@mui/icons-material/Tune';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import EmailIcon from '@mui/icons-material/Email';
import ApiIcon from '@mui/icons-material/Api';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import FlagIcon from '@mui/icons-material/Flag';
import PhoneIcon from '@mui/icons-material/Phone';
import MessageIcon from '@mui/icons-material/Message';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createAuthHeaders, handleTokenExpiration, isTokenExpired } from '../utils/auth';
import { useAuth } from '../context/AuthContext';
import IntegratedAgentPlayground from './IntegratedAgentPlayground';

const PlugAndPlayEdit = () => {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [success, setSuccess] = useState(false);
  
  // Check if we should auto-start the test
  const autoStartTest = location.state?.autoStartTest || false;
  
  // AI conversion loading states
  const [isConverting, setIsConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState('');
  const [extractingText, setExtractingText] = useState(false);
  const [convertingWithAI, setConvertingWithAI] = useState(false);
  
  const [formData, setFormData] = useState({
    agentName: '',
    language: 'en-US',
    voice: 'en-US-JennyNeural',
    knowledgeBase: null,
    knowledgeBaseFile: null,
    knowledgeBaseText: '',
    knowledgeBaseInputType: 'file',
    customPrompt: '',
    llmModel: 'gpt-4o',
    temperature: 0.7,
    speechSpeed: 100,
    handlesOrders: false,
    handlesReservations: false,
    orderSystem: '',
    reservationSystem: '',
    orderApiKey: '',
    reservationApiKey: '',
    notificationEmail: '',
    customWelcomeMessage: '',
    orderDataFields: ['customer_name', 'customer_phone', 'order_items', 'delivery_address'],
    reservationDataFields: ['customer_name', 'customer_phone', 'reservation_date', 'reservation_time', 'party_size'],
    humanEscalationEnabled: false,
    humanEscalationPhone: '',
    humanEscalationCountry: 'us'
  });

  const orderSystems = [
    { value: 'sitedish', label: 'SiteDish', description: 'Leading Dutch food ordering platform' },
    { value: 'flipdish', label: 'FlipDish', description: 'International food ordering system' },
    { value: 'other', label: 'Other/Email', description: 'Email notifications for orders' }
  ];

  const reservationSystems = [
    { value: 'zenchef', label: 'Zenchef', description: 'Restaurant reservation management' },
    { value: 'defork', label: 'DeFork', description: 'Table booking platform' },
    { value: 'other', label: 'Other/Email', description: 'Email notifications for reservations' }
  ];

  const modelOptions = [
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo', description: 'Fast and cost-effective' },
    { value: 'gpt-3.5-turbo-16k', label: 'GPT-3.5 Turbo (16k)', description: 'Extended context length' },
    { value: 'gpt-4', label: 'GPT-4', description: 'Most capable model' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo', description: 'Enhanced performance' },
    { value: 'gpt-4o', label: 'GPT-4o', description: 'Latest optimized model' },
    { value: 'gpt-4.5-preview', label: 'GPT-4.5 Preview', description: 'Next generation preview' },
    { value: 'o1-preview', label: 'o1 Preview', description: 'Advanced reasoning model' },
    { value: 'o1-mini', label: 'o1 Mini', description: 'Efficient reasoning model' }
  ];

  const languageOptions = [
    { value: 'en-US', label: 'English (US)', flag: '🇺🇸' },
    { value: 'en-GB', label: 'English (UK)', flag: '🇬🇧' },
    { value: 'en-AU', label: 'English (Australia)', flag: '🇦🇺' },
    { value: 'es-ES', label: 'Spanish', flag: '🇪🇸' },
    { value: 'fr-FR', label: 'French', flag: '🇫🇷' },
    { value: 'de-DE', label: 'German', flag: '🇩🇪' },
    { value: 'it-IT', label: 'Italian', flag: '🇮🇹' },
    { value: 'pt-BR', label: 'Portuguese (Brazil)', flag: '🇧🇷' },
    { value: 'nl-NL', label: 'Dutch', flag: '🇳🇱' },
    { value: 'ja-JP', label: 'Japanese', flag: '🇯🇵' },
    { value: 'ko-KR', label: 'Korean', flag: '🇰🇷' },
    { value: 'zh-CN', label: 'Chinese (Simplified)', flag: '🇨🇳' },
    { value: 'ru-RU', label: 'Russian', flag: '🇷🇺' },
    { value: 'ar-SA', label: 'Arabic', flag: '🇸🇦' }
  ];

  // Helper function to get country options with flags
  const getCountryOptions = () => [
    { code: "us", name: "United States", dial_code: "+1" },
    { code: "gb", name: "United Kingdom", dial_code: "+44" },
    { code: "ca", name: "Canada", dial_code: "+1" },
    { code: "au", name: "Australia", dial_code: "+61" },
    { code: "de", name: "Germany", dial_code: "+49" },
    { code: "fr", name: "France", dial_code: "+33" },
    { code: "it", name: "Italy", dial_code: "+39" },
    { code: "es", name: "Spain", dial_code: "+34" },
    { code: "pt", name: "Portugal", dial_code: "+351" },
    { code: "nl", name: "Netherlands", dial_code: "+31" },
    { code: "be", name: "Belgium", dial_code: "+32" },
    { code: "ch", name: "Switzerland", dial_code: "+41" },
    { code: "at", name: "Austria", dial_code: "+43" },
    { code: "dk", name: "Denmark", dial_code: "+45" },
    { code: "se", name: "Sweden", dial_code: "+46" },
    { code: "no", name: "Norway", dial_code: "+47" },
    { code: "fi", name: "Finland", dial_code: "+358" },
    { code: "pl", name: "Poland", dial_code: "+48" },
    { code: "cz", name: "Czech Republic", dial_code: "+420" },
    { code: "jp", name: "Japan", dial_code: "+81" },
    { code: "kr", name: "South Korea", dial_code: "+82" },
    { code: "cn", name: "China", dial_code: "+86" },
    { code: "br", name: "Brazil", dial_code: "+55" },
    { code: "mx", name: "Mexico", dial_code: "+52" },
    { code: "ar", name: "Argentina", dial_code: "+54" },
    { code: "in", name: "India", dial_code: "+91" },
    { code: "sg", name: "Singapore", dial_code: "+65" },
    { code: "za", name: "South Africa", dial_code: "+27" }
  ];

  // Enhanced country selector component with search and flags
  const CountrySelector = ({ value, onChange, ...props }) => {
    const countries = getCountryOptions();
    const [open, setOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    
    const selectedCountry = countries.find(c => c.code === value) || countries.find(c => c.code === 'us');
    
    // Filter countries based on search text
    const filteredCountries = searchText 
      ? countries.filter(c => 
          c.name.toLowerCase().includes(searchText.toLowerCase()) || 
          c.dial_code.includes(searchText)
        )
      : countries;

    const getCountryFlag = (code) => {
      const flagMap = {
        'us': '🇺🇸', 'gb': '🇬🇧', 'ca': '🇨🇦', 'au': '🇦🇺', 'de': '🇩🇪', 
        'fr': '🇫🇷', 'it': '🇮🇹', 'es': '🇪🇸', 'pt': '🇵🇹', 'nl': '🇳🇱', 
        'be': '🇧🇪', 'ch': '🇨🇭', 'at': '🇦🇹', 'dk': '🇩🇰', 'se': '🇸🇪', 
        'no': '🇳🇴', 'fi': '🇫🇮', 'pl': '🇵🇱', 'cz': '🇨🇿', 'jp': '🇯🇵', 
        'kr': '🇰🇷', 'cn': '🇨🇳', 'br': '🇧🇷', 'mx': '🇲🇽', 'ar': '🇦🇷', 
        'in': '🇮🇳', 'sg': '🇸🇬', 'za': '🇿🇦'
      };
      return flagMap[code] || '🌍';
    };

    return (
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box sx={{ position: 'relative', ...props.sx }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              minWidth: '90px',
              height: '40px',
              px: 1,
              justifyContent: 'flex-start',
              border: 'none',
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)',
                border: 'none'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '16px' }}>
                {getCountryFlag(selectedCountry.code)}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {selectedCountry.dial_code}
              </Typography>
            </Box>
          </Button>
          
          {open && (
            <Paper
              elevation={8}
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 9999,
                maxHeight: '300px',
                width: '280px',
                mt: 0.5
              }}
            >
              {/* Search Input */}
              <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search countries..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontSize: '14px' }}>🔍</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '32px',
                      fontSize: '14px'
                    }
                  }}
                />
              </Box>
              
              {/* Countries List */}
              <Box sx={{ maxHeight: '240px', overflow: 'auto' }}>
                {filteredCountries.map((country) => (
                  <Box
                    key={country.code}
                    onClick={() => {
                      onChange(country.code);
                      setOpen(false);
                      setSearchText('');
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      cursor: 'pointer',
                      bgcolor: country.code === value ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <Typography sx={{ fontSize: '16px', minWidth: '20px' }}>
                      {getCountryFlag(country.code)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '13px', minWidth: '45px', color: 'text.secondary' }}>
                      {country.dial_code}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      {country.name}
                    </Typography>
                  </Box>
                ))}
                
                {filteredCountries.length === 0 && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No countries found
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}
        </Box>
      </ClickAwayListener>
    );
  };

  const getVoiceOptions = (language) => {
    const voiceMap = {
      'en-US': [
        { value: 'en-US-JennyNeural', label: 'Jenny (Female)' },
        { value: 'en-US-GuyNeural', label: 'Guy (Male)' },
        { value: 'en-US-AriaNeural', label: 'Aria (Female)' },
        { value: 'en-US-AmberNeural', label: 'Amber (Female)' },
      ],
      'en-GB': [
        { value: 'en-GB-LibbyNeural', label: 'Libby (Female)' },
        { value: 'en-GB-RyanNeural', label: 'Ryan (Male)' },
        { value: 'en-GB-MiaNeural', label: 'Mia (Female)' },
      ],
      // ... other voice mappings same as in PlugAndPlay.js
      'nl-NL': [
        { value: 'nl-NL-FennaNeural', label: 'Fenna (Female)' },
        { value: 'nl-NL-MaartenNeural', label: 'Maarten (Male)' },
      ],
    };
   
    return voiceMap[language] || voiceMap['en-US'];
  };

  useEffect(() => {
    fetchAgentData();
  }, [agentId]);

  const fetchAgentData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/plugnplay/agent/${agentId}`, {
        headers: createAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleTokenExpiration();
          return;
        }
        throw new Error('Failed to fetch agent data');
      }

      const agent = await response.json();

      // Map agent data to form data
      setFormData({
        agentName: agent.name || '',
        language: agent.language || 'en-US',
        voice: agent.voice || 'en-US-JennyNeural',
        knowledgeBase: agent.knowledge_base_content ? JSON.parse(agent.knowledge_base_content) : null,
        knowledgeBaseText: agent.knowledge_base_type === 'text' ? agent.knowledge_base_content : '',
        knowledgeBaseInputType: agent.knowledge_base_type === 'json' ? 'file' : 'text',
        customPrompt: agent.custom_instructions || '',
        llmModel: agent.llm_model || 'gpt-4o',
        temperature: agent.temperature || 0.7,
        speechSpeed: agent.speech_speed || 100,
        handlesOrders: agent.order_management_enabled || false,
        handlesReservations: agent.reservation_enabled || false,
        orderSystem: agent.order_platform || '',
        reservationSystem: agent.reservation_platform || '',
        orderApiKey: agent.order_api_key || '',
        reservationApiKey: agent.reservation_api_key || '',
        notificationEmail: agent.email_fallback || '',
        customWelcomeMessage: agent.custom_welcome_message || '',
        orderDataFields: agent.order_data_fields || ['customer_name', 'customer_phone', 'order_items', 'delivery_address'],
        reservationDataFields: agent.reservation_data_fields || ['customer_name', 'customer_phone', 'reservation_date', 'reservation_time', 'party_size'],
        humanEscalationEnabled: agent.human_escalation_enabled || false,
        humanEscalationPhone: agent.human_escalation_phone || '',
        humanEscalationCountry: agent.human_escalation_country || 'us'
      });

    } catch (error) {
      console.error('Error fetching agent data:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load agent configuration',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/plugnplay/agent/${agentId}`, {
        method: 'PUT',
        headers: createAuthHeaders(),
        body: JSON.stringify({
          agentName: formData.agentName,
          language: formData.language,
          voice: formData.voice,
          knowledgeBase: formData.knowledgeBase,
          knowledgeBaseText: formData.knowledgeBaseText,
          speechSpeed: formData.speechSpeed,
          llmModel: formData.llmModel,
          temperature: formData.temperature,
          customPrompt: formData.customPrompt,
          handlesOrders: formData.handlesOrders,
          orderSystem: formData.orderSystem,
          orderApiKey: formData.orderApiKey,
          handlesReservations: formData.handlesReservations,
          reservationSystem: formData.reservationSystem,
          reservationApiKey: formData.reservationApiKey,
          notificationEmail: formData.notificationEmail,
          customWelcomeMessage: formData.customWelcomeMessage,
          orderDataFields: formData.orderDataFields,
          reservationDataFields: formData.reservationDataFields,
          humanEscalationEnabled: formData.humanEscalationEnabled,
          humanEscalationPhone: formData.humanEscalationPhone,
          humanEscalationCountry: formData.humanEscalationCountry
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 && isTokenExpired(data)) {
          handleTokenExpiration();
          return;
        }
        throw new Error(data.error || 'Failed to update agent');
      }

      setSuccess(true);
      setSnackbar({
        open: true,
        message: 'Agent configuration updated successfully!',
        severity: 'success'
      });

      setTimeout(() => {
        navigate('/agents');
      }, 2000);
    } catch (error) {
      console.error('Error updating agent:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update agent. Please try again.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const convertToKnowledgeBase = async (content, inputType = 'file') => {
    try {
      setIsConverting(true);
      
      // Prepare the content for ChatGPT based on input type
      let textContent = '';
      
      if (inputType === 'text') {
        textContent = content;
        setConversionProgress('Preparing text content...');
        // Add a small delay to show the progress
        await new Promise(resolve => setTimeout(resolve, 500));
      } else if (inputType === 'file') {
        // For files, we would extract text using the backend API
        setExtractingText(true);
        setConversionProgress(`Extracting text from ${content.name}...`);
        
        const formData = new FormData();
        formData.append('file', content);
        
        const extractResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/knowledge-base/extract-text`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: formData
        });
        
        if (extractResponse.ok) {
          const extractedData = await extractResponse.json();
          textContent = extractedData.text_content; // Backend returns 'text_content', not 'text'
          setExtractingText(false);
          setConversionProgress('Text extracted successfully! ✅');
          await new Promise(resolve => setTimeout(resolve, 800));
        } else {
          setExtractingText(false);
          throw new Error('Failed to extract text from file');
        }
      }

      // Call ChatGPT API to convert content to structured JSON
      setConvertingWithAI(true);
      setConversionProgress('Converting with ChatGPT AI... 🤖');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/knowledge-base/convert-with-ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          content: textContent,
          content_type: 'auto', // Backend expects 'content_type', not 'inputType'
          filename: inputType === 'file' ? content.name : 'text_input' // Backend expects 'filename', not 'fileName'
        })
      });

      if (response.ok) {
        const knowledgeBase = await response.json();
        setConvertingWithAI(false);
        setConversionProgress('Knowledge base created successfully! 🎉');
        await new Promise(resolve => setTimeout(resolve, 1000));
        return knowledgeBase;
      } else {
        setConvertingWithAI(false);
        throw new Error('Failed to convert content to knowledge base');
      }
    } catch (error) {
      console.log('ChatGPT conversion failed, using fallback data for:', inputType === 'file' ? content.name : 'text input');
      console.error('Conversion error:', error);
      
      // Reset loading states
      setExtractingText(false);
      setConvertingWithAI(false);
      setConversionProgress('Using fallback data... ⚠️');
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Return fallback mock knowledge base structure with metadata indicating it's mock data
      return {
        restaurant: {
          name: inputType === 'file' ? content.name.replace(/\.[^/.]+$/, "") : "Your Business",
          cuisine: "Modern Cuisine",
          description: "A contemporary dining experience"
        },
        categories: [
          {
            id: "items",
            name: "MENU ITEMS",
            items: [
              {
                id: "item1",
                name: "Sample Item",
                description: "Delicious menu item",
                price: 15.00,
                dietary: ["gluten-free"],
                available: true
              }
            ]
          }
        ],
        hours: {
          monday: "9:00 AM - 9:00 PM",
          tuesday: "9:00 AM - 9:00 PM",
          wednesday: "9:00 AM - 9:00 PM",
          thursday: "9:00 AM - 9:00 PM",
          friday: "9:00 AM - 10:00 PM",
          saturday: "9:00 AM - 10:00 PM",
          sunday: "10:00 AM - 8:00 PM"
        },
        contact: {
          phone: "Phone number to be configured",
          email: "info@yourbusiness.com",
          address: "Your business address"
        },
        _metadata: {
          generated_with_ai: false,
          fallback_reason: error.message,
          source: inputType === 'file' ? content.name : 'text input',
          backend_available: false
        }
      };
    } finally {
      // Always reset loading states
      setIsConverting(false);
      setExtractingText(false);
      setConvertingWithAI(false);
      setConversionProgress('');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, knowledgeBaseFile: file, knowledgeBaseInputType: 'file' }));
      // Don't automatically process - wait for user to click "Process with AI"
    }
  };

  const handleTextConversion = async () => {
    if (formData.knowledgeBaseText.trim()) {
      try {
        const knowledgeBase = await convertToKnowledgeBase(formData.knowledgeBaseText, 'text');
        setFormData(prev => ({ ...prev, knowledgeBase }));
        
        // Check if this was generated with AI or fallback data
        if (knowledgeBase._metadata?.generated_with_ai === false) {
          setSnackbar({
            open: true,
            message: 'Using sample knowledge base (AI conversion unavailable - backend server needs restart)',
            severity: 'warning'
          });
        } else {
          setSnackbar({
            open: true,
            message: '🎉 Knowledge base created with AI successfully! Your content has been structured and is ready to use.',
            severity: 'success'
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to process text. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleFileConversion = async () => {
    if (formData.knowledgeBaseFile) {
      try {
        const knowledgeBase = await convertToKnowledgeBase(formData.knowledgeBaseFile, 'file');
        setFormData(prev => ({ ...prev, knowledgeBase }));
        
        // Check if this was generated with AI or fallback data
        if (knowledgeBase._metadata?.generated_with_ai === false) {
          setSnackbar({
            open: true,
            message: 'Using sample knowledge base (AI conversion unavailable - backend server needs restart)',
            severity: 'warning'
          });
        } else {
          setSnackbar({
            open: true,
            message: '🎉 Knowledge base created from file with AI successfully! Your content has been extracted and structured.',
            severity: 'success'
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to process file. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/agents')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" gutterBottom>
            Edit Plug & Play Agent
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure your voice agent settings
          </Typography>
        </Box>
        
        {/* Top Right Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Agent Test Component */}
          {agentId && (
            <IntegratedAgentPlayground 
              agent={{ 
                id: agentId, 
                name: formData.agentName || `Agent ${agentId}`,
                is_plugnplay: true 
              }}
              autoStart={autoStartTest}
              onUsageUpdate={(usage) => {
                console.log('Agent usage:', usage);
                // Could implement usage tracking here
              }}
            />
          )}
          
          {/* Save/Cancel Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/agents')}
              disabled={saving}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                minWidth: 100,
                height: 36
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !formData.agentName}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                minWidth: 120,
                height: 36
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Box>

      {success ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 3 }} />
          <Typography variant="h4" gutterBottom>
            🎉 Agent Updated Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your voice agent configuration has been saved
          </Typography>
          <Stack spacing={2} alignItems="center">
            <Typography variant="body2">
              Redirecting to agents dashboard...
            </Typography>
            <CircularProgress size={24} />
          </Stack>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Basic Settings */}
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Basic Settings
              </Typography>
              
              <TextField
                fullWidth
                label="Agent Name"
                value={formData.agentName}
                onChange={(e) => setFormData({ ...formData, agentName: e.target.value })}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Card>
          </Grid>

          {/* Language & Voice */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                <LanguageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Language & Voice
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Language</InputLabel>
                <Select
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                >
                  {languageOptions.map((lang) => (
                    <MenuItem key={lang.value} value={lang.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: 8 }}>{lang.flag}</span>
                        {lang.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Voice</InputLabel>
                <Select
                  value={formData.voice}
                  onChange={(e) => setFormData({ ...formData, voice: e.target.value })}
                >
                  {getVoiceOptions(formData.language).map((voice) => (
                    <MenuItem key={voice.value} value={voice.value}>
                      {voice.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="body2" gutterBottom>Speech Speed (%)</Typography>
              <TextField
                fullWidth
                type="number"
                value={formData.speechSpeed}
                onChange={(e) => setFormData({ ...formData, speechSpeed: parseInt(e.target.value) || 100 })}
                inputProps={{ min: 50, max: 200, step: 5 }}
                helperText="50-200% (100% is normal speed)"
                sx={{ mb: 2 }}
              />
            </Card>
          </Grid>

          {/* AI Model & Settings */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                <SmartToyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                AI Model & Settings
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>AI Model</InputLabel>
                <Select
                  value={formData.llmModel}
                  onChange={(e) => setFormData({ ...formData, llmModel: e.target.value })}
                >
                  {modelOptions.map((model) => (
                    <MenuItem key={model.value} value={model.value}>
                      <Box>
                        <Typography variant="body2">{model.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {model.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="body2" gutterBottom>
                Temperature (Creativity)
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={formData.temperature}
                onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) || 0.7 })}
                inputProps={{ min: 0, max: 1, step: 0.1 }}
                helperText="0.0 (precise) to 1.0 (creative)"
                sx={{ mb: 2 }}
              />
            </Card>
          </Grid>

          {/* Knowledge Base */}
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <MenuBookIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Knowledge Base
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Update your menu, service information, or any content your agent should know about
              </Typography>

              {/* Input Type Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Choose Input Method
                </Typography>
                <FormControlLabel
                  control={
                    <Radio
                      checked={formData.knowledgeBaseInputType === 'file'}
                      onChange={() => setFormData(prev => ({ ...prev, knowledgeBaseInputType: 'file' }))}
                    />
                  }
                  label="Upload File (PDF, TXT, DOCX, Images)"
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={formData.knowledgeBaseInputType === 'text'}
                      onChange={() => setFormData(prev => ({ ...prev, knowledgeBaseInputType: 'text' }))}
                    />
                  }
                  label="Enter Text Directly"
                />
              </Box>

              {/* File Upload Option */}
              {formData.knowledgeBaseInputType === 'file' && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ p: 4, textAlign: 'center', border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                    <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Upload Your Content
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Supported formats: PDF, TXT, DOCX, Images (JPG, PNG)
                    </Typography>
                    
                    <input
                      accept=".pdf,.txt,.docx,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      id="file-upload-edit"
                      type="file"
                      onChange={handleFileUpload}
                    />
                    <label htmlFor="file-upload-edit">
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={<UploadFileIcon />}
                        size="large"
                      >
                        Choose File
                      </Button>
                    </label>

                    {formData.knowledgeBaseFile && (
                      <Alert severity="success" sx={{ mt: 3 }}>
                        <Typography variant="body2">
                          ✅ {formData.knowledgeBaseFile.name} uploaded successfully
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mt: 2 }}
                          onClick={handleFileConversion}
                          disabled={isConverting}
                          startIcon={isConverting ? <CircularProgress size={16} /> : <SmartToyIcon />}
                        >
                          {isConverting ? 'Processing...' : 'Process with AI'}
                        </Button>
                  </Alert>
                    )}

                    {/* Conversion Progress Indicator */}
                    {isConverting && (
                      <Box sx={{ mt: 3 }}>
                        <LinearProgress sx={{ mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          {conversionProgress}
                        </Typography>
                        
                        {/* Specific progress indicators */}
                        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                          <Chip
                            icon={extractingText ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                            label="Extract Text"
                            color={extractingText ? "primary" : "success"}
                            variant={extractingText ? "outlined" : "filled"}
                          />
                          <Chip
                            icon={convertingWithAI ? <CircularProgress size={16} /> : <SmartToyIcon />}
                            label="AI Conversion"
                            color={convertingWithAI ? "primary" : "default"}
                            variant={convertingWithAI ? "outlined" : "filled"}
                          />
                        </Stack>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Text Input Option */}
              {formData.knowledgeBaseInputType === 'text' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Enter Your Business Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Describe your menu, services, hours, location, or any information your agent should know
                    </Typography>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    placeholder="Example: We are an Italian restaurant located in Amsterdam. Our specialties include homemade pasta, wood-fired pizza, and authentic gelato. We're open Tuesday-Sunday, 5 PM to 11 PM. We offer delivery through Deliveroo and takeout orders. Our signature dishes include Pasta Carbonara (€16), Margherita Pizza (€14), and Tiramisu (€8)..."
                    value={formData.knowledgeBaseText}
                    onChange={(e) => setFormData(prev => ({ ...prev, knowledgeBaseText: e.target.value }))}
                    sx={{ mb: 2 }}
                  />
                  
                  <Button
                    variant="contained"
                    onClick={handleTextConversion}
                    disabled={!formData.knowledgeBaseText.trim() || isConverting}
                    startIcon={isConverting ? <CircularProgress size={16} /> : <SmartToyIcon />}
                  >
                    {isConverting ? 'Converting...' : 'Convert to Knowledge Base with AI'}
                  </Button>

                  {/* Progress indicator for text conversion */}
                  {isConverting && (
                    <Box sx={{ mt: 3 }}>
                      <LinearProgress sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary" align="center">
                        {conversionProgress}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Generated Knowledge Base Preview */}
              {formData.knowledgeBase && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <MenuBookIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Generated Knowledge Base Preview
                  </Typography>
                  
                  {formData.knowledgeBase._metadata?.generated_with_ai === false && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Using Sample Data:</strong> The AI conversion endpoints are not available (backend server needs restart). 
                        This is sample data for demonstration. Restart your backend server to enable real AI-powered conversion.
                      </Typography>
                    </Alert>
                  )}
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {formData.knowledgeBase._metadata?.generated_with_ai === false 
                      ? 'Sample knowledge base structure for demonstration. You can regenerate this with new content above.'
                      : 'AI has automatically structured your content. You can regenerate this with new content above.'}
                  </Typography>
                  
                  <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
                    <pre style={{ fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {JSON.stringify(formData.knowledgeBase, null, 2)}
                    </pre>
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {formData.knowledgeBase._metadata?.generated_with_ai === false 
                      ? '⚠️ Using sample data (AI conversion unavailable - restart backend server to enable real AI conversion)' 
                      : '✅ Generated using real AI conversion'}
                  </Typography>
                </Box>
              )}

              {/* Info Message */}
              {!formData.knowledgeBase && !formData.knowledgeBaseText && (
                <Alert severity="info">
                  <Typography variant="body2">
                    💡 <strong>Update your knowledge base:</strong> Choose a method above to update your agent's knowledge. 
                    The AI will still be able to handle basic conversations without a knowledge base.
                  </Typography>
                </Alert>
              )}
            </Card>
          </Grid>

          {/* Custom Prompt */}
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <TuneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Custom Instructions
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Describe how your agent should behave and what it should know about your business"
                value={formData.customPrompt}
                onChange={(e) => setFormData({ ...formData, customPrompt: e.target.value })}
                helperText="Provide detailed instructions for how your agent should behave and respond to customers"
              />
            </Card>
          </Grid>

          {/* Welcome Message */}
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <RecordVoiceOverIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Welcome Message
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Customize the first message your agent says to greet callers
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder={
                  formData.language.startsWith('nl') ? 
                    "Goedendag, welkom bij [Restaurant Name]. Waarmee kan ik u vandaag van dienst zijn?" :
                  formData.language.startsWith('es') ?
                    "Hola, bienvenido a [Restaurant Name]. ¿Cómo puedo ayudarle hoy?" :
                  formData.language.startsWith('fr') ?
                    "Bonjour, bienvenue chez [Restaurant Name]. Comment puis-je vous aider aujourd'hui?" :
                  formData.language.startsWith('de') ?
                    "Guten Tag, willkommen bei [Restaurant Name]. Wie kann ich Ihnen heute helfen?" :
                    "Hello, welcome to [Restaurant Name]. How can I help you today?"
                }
                value={formData.customWelcomeMessage}
                onChange={(e) => setFormData({ ...formData, customWelcomeMessage: e.target.value })}
                helperText="Leave empty to use the default greeting in your selected language"
              />
            </Card>
          </Grid>

          {/* Business Integration */}
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Business Integration
              </Typography>
              
              <Grid container spacing={3}>
                {/* Orders */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    <DeliveryDiningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Orders & Delivery
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.handlesOrders}
                        onChange={(e) => setFormData({ ...formData, handlesOrders: e.target.checked })}
                      />
                    }
                    label="Handle order taking"
                  />
                  
                  {formData.handlesOrders && (
                    <Box sx={{ mt: 2 }}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Order System</InputLabel>
                        <Select
                          value={formData.orderSystem}
                          onChange={(e) => setFormData({ ...formData, orderSystem: e.target.value })}
                        >
                          {orderSystems.map((system) => (
                            <MenuItem key={system.value} value={system.value}>
                              {system.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      {formData.orderSystem && formData.orderSystem !== 'other' && (
                        <TextField
                          fullWidth
                          label="API Key"
                          value={formData.orderApiKey}
                          onChange={(e) => setFormData({ ...formData, orderApiKey: e.target.value })}
                          InputProps={{
                            startAdornment: <ApiIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          }}
                          sx={{ mb: 2 }}
                        />
                      )}
                      
                      {formData.orderSystem === 'other' && (
                        <TextField
                          fullWidth
                          label="Notification Email"
                          value={formData.notificationEmail}
                          onChange={(e) => setFormData({ ...formData, notificationEmail: e.target.value })}
                          InputProps={{
                            startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          }}
                          sx={{ mb: 2 }}
                        />
                      )}
                      
                      {/* Data Collection Options for Orders */}
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" gutterBottom>
                          Data to collect for orders:
                        </Typography>
                        <FormControl component="fieldset" sx={{ mt: 1 }}>
                          <FormGroup>
                            {[
                              { value: 'customer_name', label: 'Customer Name' },
                              { value: 'customer_phone', label: 'Customer Phone Number' },
                              { value: 'customer_email', label: 'Customer Email' },
                              { value: 'order_items', label: 'Order Items' },
                              { value: 'delivery_address', label: 'Delivery Address' },
                              { value: 'special_instructions', label: 'Special Instructions' },
                              { value: 'preferred_delivery_time', label: 'Preferred Delivery Time' }
                            ].map((field) => (
                              <FormControlLabel
                                key={field.value}
                                control={
                                  <Checkbox
                                    checked={formData.orderDataFields.includes(field.value)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFormData({
                                          ...formData,
                                          orderDataFields: [...formData.orderDataFields, field.value]
                                        });
                                      } else {
                                        setFormData({
                                          ...formData,
                                          orderDataFields: formData.orderDataFields.filter(f => f !== field.value)
                                        });
                                      }
                                    }}
                                  />
                                }
                                label={field.label}
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Box>
                    </Box>
                  )}
                </Grid>

                {/* Reservations */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    <BookOnlineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Reservations
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.handlesReservations}
                        onChange={(e) => setFormData({ ...formData, handlesReservations: e.target.checked })}
                      />
                    }
                    label="Handle reservations"
                  />
                  
                  {formData.handlesReservations && (
                    <Box sx={{ mt: 2 }}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Reservation System</InputLabel>
                        <Select
                          value={formData.reservationSystem}
                          onChange={(e) => setFormData({ ...formData, reservationSystem: e.target.value })}
                        >
                          {reservationSystems.map((system) => (
                            <MenuItem key={system.value} value={system.value}>
                              {system.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      {formData.reservationSystem && formData.reservationSystem !== 'other' && (
                        <TextField
                          fullWidth
                          label="API Key"
                          value={formData.reservationApiKey}
                          onChange={(e) => setFormData({ ...formData, reservationApiKey: e.target.value })}
                          InputProps={{
                            startAdornment: <ApiIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          }}
                          sx={{ mb: 2 }}
                        />
                      )}
                      
                      {formData.reservationSystem === 'other' && (
                        <TextField
                          fullWidth
                          label="Notification Email"
                          value={formData.notificationEmail}
                          onChange={(e) => setFormData({ ...formData, notificationEmail: e.target.value })}
                          InputProps={{
                            startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          }}
                          sx={{ mb: 2 }}
                        />
                      )}
                      
                      {/* Data Collection Options for Reservations */}
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" gutterBottom>
                          Data to collect for reservations:
                        </Typography>
                        <FormControl component="fieldset" sx={{ mt: 1 }}>
                          <FormGroup>
                            {[
                              { value: 'customer_name', label: 'Customer Name' },
                              { value: 'customer_phone', label: 'Customer Phone Number' },
                              { value: 'customer_email', label: 'Customer Email' },
                              { value: 'reservation_date', label: 'Reservation Date' },
                              { value: 'reservation_time', label: 'Reservation Time' },
                              { value: 'party_size', label: 'Number of People' },
                              { value: 'special_requests', label: 'Special Requests' },
                              { value: 'occasion', label: 'Occasion (Birthday, Anniversary, etc.)' }
                            ].map((field) => (
                              <FormControlLabel
                                key={field.value}
                                control={
                                  <Checkbox
                                    checked={formData.reservationDataFields.includes(field.value)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFormData({
                                          ...formData,
                                          reservationDataFields: [...formData.reservationDataFields, field.value]
                                        });
                                      } else {
                                        setFormData({
                                          ...formData,
                                          reservationDataFields: formData.reservationDataFields.filter(f => f !== field.value)
                                        });
                                      }
                                    }}
                                  />
                                }
                                label={field.label}
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Box>
                    </Box>
                  )}
          </Grid>

                {/* Human Escalation */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    <SupportAgentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Human Escalation
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.humanEscalationEnabled}
                        onChange={(e) => setFormData({ ...formData, humanEscalationEnabled: e.target.checked })}
                      />
                    }
                    label="Enable human escalation"
                  />
                  
                  {formData.humanEscalationEnabled && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        Phone number to transfer calls to:
                      </Typography>
                      
                      <TextField
                        fullWidth
                        label="Phone Number"
                        placeholder="Enter phone number"
                        value={formData.humanEscalationPhone}
                        onChange={(e) => setFormData({ ...formData, humanEscalationPhone: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                <CountrySelector 
                                  value={formData.humanEscalationCountry}
                                  onChange={(code) => setFormData({ ...formData, humanEscalationCountry: code })}
                                  sx={{ 
                                    minWidth: 100,
                                    '& .MuiOutlinedInput-root': {
                                      border: 'none',
                                      '& fieldset': { border: 'none' },
                                      '&:hover fieldset': { border: 'none' },
                                      '&.Mui-focused fieldset': { border: 'none' }
                                    }
                                  }}
                                />
                                <Box sx={{ width: 1, height: 24, bgcolor: 'divider', mx: 1 }} />
            </Box>
                            </InputAdornment>
                          ),
                        }}
                        helperText="Number where calls should be transferred when customers request human assistance"
                        sx={{ mb: 2 }}
                      />
                      
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>How it works:</strong> When customers say phrases like "I want to speak to someone", 
                          "Can I talk to a human", or "Transfer me to a person", the agent will automatically 
                          transfer the call to this number.
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                </Grid>

              </Grid>
            </Card>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlugAndPlayEdit; 